import {
  SELECT_CREATE_OPTION_KEY_PREFIX,
  SELECT_SELECTED_PREFIX,
  SELECT_TAG_INPUT_PLACEHOLDER,
} from 'ui/select/select.constants';
import type { SelectOption } from 'ui/select/select.types';

export interface SelectCreateOption extends SelectOption {
  createdValue: string;
}

interface SelectPlaceholderInput {
  placeholder: string;
  disabled: boolean;
  multiple: boolean;
  hasValue: boolean;
  isTagInput: boolean;
}

export const formatCreateLabel = (inputValue: string) => `Use "${inputValue}"`;

export const makeCreateOption = (inputValue: string): SelectCreateOption => ({
  label: formatCreateLabel(inputValue),
  value: inputValue,
  createdValue: inputValue,
});

const isCreateOption = (option: SelectOption): option is SelectCreateOption =>
  'createdValue' in option;

export const toPlainOption = (option: SelectOption): SelectOption =>
  isCreateOption(option)
    ? { label: option.createdValue, value: option.createdValue }
    : option;

export const getOptionLabel = (option: SelectOption) => option.label;

export const getOptionSearchText = (option: SelectOption) =>
  `${option.label} ${option.value}`;

export const getOptionKey = (option: SelectOption) =>
  isCreateOption(option)
    ? `${SELECT_CREATE_OPTION_KEY_PREFIX}${option.createdValue}`
    : String(option.value);

export const isSameOption = (option: SelectOption, value: SelectOption) =>
  option.value === value.value;

export const toOptionList = (
  selected: SelectOption | SelectOption[] | null
): SelectOption[] => {
  if (!selected) return [];

  return Array.isArray(selected) ? selected : [selected];
};

const isOptionObject = (value: unknown): value is SelectOption =>
  typeof value === 'object' && value !== null && 'value' in value;

const resolveOne = (
  options: SelectOption[],
  value: unknown
): SelectOption | null => {
  if (isOptionObject(value)) return value;
  if (value === null || value === undefined) return null;

  return (
    options.find(
      (option) => option.value === value || option.label === value
    ) ?? null
  );
};

export const resolveSelectValue = (
  value: unknown,
  options: SelectOption[],
  multiple: boolean
): SelectOption | SelectOption[] | null => {
  if (Array.isArray(value)) {
    const list = value
      .map((item) => resolveOne(options, item))
      .filter((item): item is SelectOption => !!item);

    return multiple ? list : list[0] ?? null;
  }

  const option = resolveOne(options, value);

  if (!multiple) return option;

  return option ? [option] : [];
};

export const withSelectedOptions = (
  options: SelectOption[],
  selected: SelectOption[]
): SelectOption[] => {
  const missing = selected.filter(
    (item) => !options.some((option) => option.value === item.value)
  );

  return missing.length ? [...options, ...missing] : options;
};

export const getSelectedSummary = (
  selected: SelectOption | SelectOption[] | null
) => {
  const labels = toOptionList(selected).map(getOptionLabel);

  return labels.length ? `${SELECT_SELECTED_PREFIX}${labels.join(', ')}` : '';
};

export const getSelectPlaceholder = ({
  placeholder,
  disabled,
  multiple,
  hasValue,
  isTagInput,
}: SelectPlaceholderInput) => {
  if (disabled || (multiple && hasValue)) return undefined;

  return isTagInput ? SELECT_TAG_INPUT_PLACEHOLDER : placeholder;
};
